import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Pause, Play, Send } from "lucide-react";
import { toast } from "sonner";

const defaultBody = `Hi {{full_name}},

We thought you'd love this: {{title}} — {{destination}}.
Travel dates: {{start_date}} to {{end_date}}
Price: ₹{{price}}

{{description}}

Cheers,
Travelhostic`;

const empty = {
  name: "",
  package_id: "",
  traveler_ids: [],
  schedule_type: "one_time",
  subject: "Your next adventure: {{title}}",
  body_html: defaultBody,
  send_at: "",
  recurrence_unit: "weekly",
  recurrence_interval: 1,
  start_at: "",
  months_after: 3,
};

export default function Schedules() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [travelers, setTravelers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, p, t] = await Promise.all([api.get("/schedules"), api.get("/packages"), api.get("/travelers")]);
      setItems(s.data);
      setPackages(p.data);
      setTravelers(t.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({
      ...empty,
      ...s,
      send_at: s.send_at ? s.send_at.slice(0, 16) : "",
      start_at: s.start_at ? s.start_at.slice(0, 16) : "",
      traveler_ids: s.traveler_ids || [],
    });
    setEditingId(s.id);
    setOpen(true);
  };

  const toggleTraveler = (id) => {
    setForm((f) => {
      const exists = f.traveler_ids.includes(id);
      return { ...f, traveler_ids: exists ? f.traveler_ids.filter((x) => x !== id) : [...f.traveler_ids, id] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        package_id: form.package_id,
        traveler_ids: form.traveler_ids,
        schedule_type: form.schedule_type,
        subject: form.subject,
        body_html: form.body_html,
        send_at: form.schedule_type === "one_time" && form.send_at ? new Date(form.send_at).toISOString() : null,
        recurrence_unit: form.schedule_type === "recurring" ? form.recurrence_unit : null,
        recurrence_interval: form.schedule_type === "recurring" ? parseInt(form.recurrence_interval) || 1 : null,
        start_at: form.schedule_type === "recurring" && form.start_at ? new Date(form.start_at).toISOString() : null,
        months_after: form.schedule_type === "anniversary" ? parseInt(form.months_after) || 0 : null,
      };
      if (editingId) {
        await api.put(`/schedules/${editingId}`, payload);
        toast.success("Schedule updated");
      } else {
        await api.post("/schedules", payload);
        toast.success("Schedule created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this schedule?")) return;
    await api.delete(`/schedules/${id}`);
    toast.success("Deleted");
    load();
  };

  const togglePause = async (s) => {
    const path = s.status === "active" ? "pause" : "resume";
    await api.post(`/schedules/${s.id}/${path}`);
    load();
  };

  const sendNow = async (id) => {
    try {
      const { data } = await api.post(`/schedules/${id}/send-now`);
      toast.success(`Dispatched ${data.sent} email(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="schedules-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Campaigns</div>
          <h1 className="font-display text-4xl mt-2 text-[#0F172A]">Email Scheduler</h1>
          <p className="text-[#64748B] mt-2 text-sm">One-time, recurring, or follow-up emails powered by ZeptoMail.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#1E3A8A] hover:bg-[#172A6B] text-white" data-testid="add-schedule-btn">
              <Plus size={16} className="mr-2" /> New schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editingId ? "Edit schedule" : "Create schedule"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="schedule-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Package</Label>
                  <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                    <SelectTrigger data-testid="schedule-package-select"><SelectValue placeholder="Select package" /></SelectTrigger>
                    <SelectContent>
                      {packages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.title} — {p.destination}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 col-span-2">
                  <Label>Schedule type</Label>
                  <Select value={form.schedule_type} onValueChange={(v) => setForm({ ...form, schedule_type: v })}>
                    <SelectTrigger data-testid="schedule-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">One-time</SelectItem>
                      <SelectItem value="recurring">Recurring</SelectItem>
                      <SelectItem value="anniversary">Anniversary (months after package end)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.schedule_type === "one_time" && (
                  <div className="space-y-2 col-span-2">
                    <Label>Send at</Label>
                    <Input type="datetime-local" value={form.send_at} onChange={(e) => setForm({ ...form, send_at: e.target.value })} data-testid="schedule-sendat-input" />
                  </div>
                )}

                {form.schedule_type === "recurring" && (
                  <>
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select value={form.recurrence_unit} onValueChange={(v) => setForm({ ...form, recurrence_unit: v })}>
                        <SelectTrigger data-testid="schedule-unit-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Every (N)</Label>
                      <Input type="number" min={1} value={form.recurrence_interval} onChange={(e) => setForm({ ...form, recurrence_interval: e.target.value })} data-testid="schedule-interval-input" />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Start at</Label>
                      <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="schedule-startat-input" />
                    </div>
                  </>
                )}

                {form.schedule_type === "anniversary" && (
                  <div className="space-y-2 col-span-2">
                    <Label>Months after package end date</Label>
                    <Input type="number" min={1} value={form.months_after} onChange={(e) => setForm({ ...form, months_after: e.target.value })} data-testid="schedule-months-input" />
                    <p className="text-xs text-[#64748B]">e.g. 3 = send today to travelers whose package ended exactly 3 months ago.</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Recipients</Label>
                <div className="border border-[#E2E8F0] rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                  <p className="text-xs text-[#64748B] mb-2">Leave all unchecked to send to ALL travelers.</p>
                  {travelers.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.traveler_ids.includes(t.id)}
                        onChange={() => toggleTraveler(t.id)}
                        data-testid={`schedule-traveler-${t.id}`}
                      />
                      {t.full_name} <span className="text-[#64748B]">({t.email})</span>
                    </label>
                  ))}
                  {travelers.length === 0 && <p className="text-sm text-[#64748B]">No travelers yet.</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="schedule-subject-input" />
              </div>
              <div className="space-y-2">
                <Label>Body (supports {"{{full_name}}, {{title}}, {{destination}}, {{start_date}}, {{end_date}}, {{price}}, {{description}}"})</Label>
                <Textarea rows={8} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} data-testid="schedule-body-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="schedule-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#172A6B] text-white" data-testid="schedule-save-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl card-soft overflow-hidden" data-testid="schedules-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Next run</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-[#64748B]">No schedules yet.</TableCell>
              </TableRow>
            ) : (
              items.map((s) => (
                <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell><span className="text-xs uppercase tracking-widest text-[#64748B]">{s.schedule_type.replace("_", " ")}</span></TableCell>
                  <TableCell>
                    {s.schedule_type === "anniversary" ? "Daily check" : s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2.5 py-1 rounded-full ${
                      s.status === "active" ? "bg-[#16A34A]/10 text-[#16A34A]" :
                      s.status === "paused" ? "bg-[#F59E0B]/10 text-[#F59E0B]" :
                      "bg-[#64748B]/10 text-[#64748B]"
                    }`}>{s.status}</span>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => sendNow(s.id)} data-testid={`sendnow-${s.id}`} title="Send now">
                      <Send size={14} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => togglePause(s)} data-testid={`pause-${s.id}`}>
                      {s.status === "active" ? <Pause size={14} /> : <Play size={14} />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-schedule-${s.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(s.id)} data-testid={`delete-schedule-${s.id}`}>
                      <Trash2 size={14} className="text-[#DC2626]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
